import React from "react";
import Title from "../../ui/Title";

const FamilyCaregivers = () => {
  return (
    <div id="growing-aging-population" className="scroll-mt-24">
      <Title
        title="The growing aging population and the decline in the number of potential family caregivers"
        className="mb-6"
      />

      <div className="space-y-4 text-softBlack leading-loose text-base font-normal">
        <p>
          Family structures around the world are changing due to broader
          socio-cultural shifts. According to a{" "}
          <a
            href="http://www.cpa.org.uk/information/reviews/CPA-Rapid-Review-Changing-family-structures.pdf"
            className="text-blue underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            review article
          </a>{" "}
          by the UK-based nonprofit Centre for Policy and Ageing, ‘the shift
          towards a low‐mortality/low‐fertility society will result in an
          increased number of living generations and a decrease in the number of
          living relatives within each generation.’ This demographic change will
          lead to ‘vertical multigenerational families’, with fewer people in
          each generation. These changes will have a substantial impact on
          caregiving, especially when it comes to the care of older adults.
        </p>
        <p>
          Due to advancements in medical science, better living environments,
          and better quality of life, life expectancy is much higher than in the
          past. This will result in a growing aging population and a decline in
          the number of children and potential relatives who could be
          caregivers. Hence, the number of people who need care will increase
          drastically and the number of people who could potentially provide
          care will decline. This is a major concern if we move into the future
          without actively addressing this evolving landscape.
        </p>
        <p className="italic">
          Here are some{" "}
          <a
            href="https://www.who.int/news-room/fact-sheets/detail/ageing-and-health"
            className="text-blue underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            data
          </a>{" "}
          from the World Health Organization (WHO) on the growing aging
          population:
        </p>
        <ul className="list-disc list-inside space-y-2 ml-4">
          <li>
            By 2030, one in six people in the world will be aged 60 years or
            over.
          </li>
          <li>
            The share of the population aged 60 years and over will increase
            from 1 billion in 2020 to 1.4 billion in 2030.
          </li>
          <li>
            By 2050, the world’s population of people aged 60 years and older
            will increase to 2.1 billion.
          </li>
          <li>
            The number of persons aged 80 years or older is expected to triple
            between 2020 and 2050 to reach 426 million.
          </li>
        </ul>
        <p>
          The demographic changes will also lead to a decline in the number of
          potential caregivers in the family for older adults. For instance,{" "}
          <a
            href="https://www.caregiver.org/uploads/2023/02/baby-boom-and-the-growing-care-gap-insight-AARP-ppi-ltc.pdf"
            className="text-blue underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            data from the United States
          </a>{" "}
          by the AARP Public Policy Institute show that the number of potential
          caregivers for a person in old age is projected to decline from 6.1 in
          2020 to 4.1 in 2030. The decline will be sharp from 2030-2050, with
          the number plummeting from 4.1 to 2.9.
        </p>
        <p>
          Since there will be fewer family members available to provide care for
          older people, the need for institutional care will rise. However,
          around the world, there are not yet adequate systems in place to care
          for older adults, and the high reliance on family members to take care
          of older adults will continue.
        </p>
        <p>
          According to the{" "}
          <a
            href="https://desapublications.un.org/policy-briefs/un-desa-policy-brief-no-143-caregiving-ageing-world"
            className="text-blue underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            UN DESA policy brief
          </a>{" "}
          <span className="italic">Caregiving in an Ageing World</span>, the
          availability of paid long-term caregivers is not sufficient to meet
          the increasing demand. The severe shortage of paid and unpaid
          caregivers points to a looming crisis when it comes to care. ‘Care
          Crisis’ is a term that is being increasingly used to refer to the
          increasing shortage of caregivers and quality care, especially for
          vulnerable groups like older adults.
        </p>

        <p className="italic">
          The UN DESA policy brief also suggests a few actions which need to be
          taken to address the emerging crisis:
        </p>
        <ul className="list-disc list-inside space-y-2 ml-4">
          <li>
            It is essential to create new legislation that is consistent with
            international labor standards to protect care workers and address
            inequalities in working conditions.
          </li>
          <li>
            There is a need to invest in formal long-term care infrastructure
            and make long-term care work an attractive employment proposition.
          </li>
          <li>
            Care and support systems need to be person-centered, suiting the
            needs of older adults and their caregivers. The brief recommends
            that ‘support systems must go beyond medical aspects of care,
            understanding the unique experiences of an individual’s culture,
            life history, and social support network.’
          </li>
        </ul>

        <div className="flex flex-col items-center justify-center">
          <img src="/assets/img/ltc.png" alt="Long Term Care" />
          <p className="text-xs text-softBlack font-normal">
            Source:
            <a
              href="https://www.un.org/development/desa/dpad/publication/un-desa-policy-brief-no-143-caregiving-in-an-ageing-world/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue underline"
            >
              UN DESA Policy Brief No. 143: Caregiving in an Ageing World
            </a>{" "}
          </p>
        </div>
      </div>
    </div>
  );
};

export default FamilyCaregivers;
